import { useCallback, useMemo } from "react";
import { generatePath } from "react-router";

import type { GoogleFreeBookingLinksSettingsI18nInput } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { FeatureIslandHeader } from "@/components/features/_layouts";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";

import { useUpdateGoogleFreeBookingLinksSettingsI18n } from "../../../api";
import { useGoogleFreeBookingLinksSettingsI18nContext } from "../_context";

export const Header = () => {
	const { updateGoogleFreeBookingLinksSettingsI18n } = useUpdateGoogleFreeBookingLinksSettingsI18n();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { form } = useGoogleFreeBookingLinksSettingsI18nContext();
	const { getValues, watch, reset } = form;
	const hotelID = getValues("hotelID");
	const langCode = getValues("lang");
	const { isDirty, isValid } = form.formState;

	const buttonVariant = useMemo<ButtonProps["variant"]>(
		() => (isDirty && isValid ? "default" : "disabled"),
		[isDirty, isValid],
	);

	const handleClick = useCallback(async () => {
		showLoadingScreenCallback();
		const settings = watch();
		const input: GoogleFreeBookingLinksSettingsI18nInput = {
			hotelID: hotelID,
			lang: langCode,
			address: settings.address,
			hotelName: settings.hotelName,
		} satisfies GoogleFreeBookingLinksSettingsI18nInput;

		const { data, errors } = await updateGoogleFreeBookingLinksSettingsI18n(input).finally(() =>
			hideLoadingScreenCallback(),
		);

		if (data && !errors) {
			reset(settings);
			notifyUserEventInfo({
				title: "Google無料予約リンク設定を更新しました",
			});
		}
	}, [
		hideLoadingScreenCallback,
		hotelID,
		langCode,
		reset,
		showLoadingScreenCallback,
		updateGoogleFreeBookingLinksSettingsI18n,
		watch,
	]);

	return (
		<FeatureIslandHeader
			title="Google 無料予約リンク設定"
			navigator={{
				default: generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.DEFAULT, {
					hotelID,
				}),
				i18n: (langCode) => {
					return generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.I18N, {
						hotelID,
						langCode,
					});
				},
			}}
			activeLangCode={langCode}
			rightChildren={
				<Button variant={buttonVariant} type="button" size="xl" onClick={handleClick}>
					保存
				</Button>
			}
		/>
	);
};
